import {
  convertCssVariables,
  createTheme,
  DEFAULT_THEME,
  defaultCssVariablesResolver,
  defaultVariantColorsResolver,
  mergeMantineTheme,
  type VariantColorsResolver,
} from '@mantine/core'

import { colors } from './colors'
import * as components from './components'

const variantColorResolver: VariantColorsResolver = (input) => {
  if (input.variant === 'neutral') {
    return {
      background: 'transparent',
      hover:
        'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
      color: 'var(--mantine-color-text)',
      border: 'none',
    }
  }
  return defaultVariantColorsResolver(input)
}

// This overrides settings in the default theme which you can see here:
// https://github.com/mantinedev/mantine/blob/master/src/mantine-styles/src/theme/default-theme.ts
export const theme = createTheme({
  fontFamily: '"Lato", sans-serif',
  fontFamilyMonospace: '"Courier Prime", courier, monospace',
  headings: {
    fontFamily: '"Montserrat", sans-serif',
    sizes: {
      h1: { fontSize: '32px', lineHeight: '1.3', fontWeight: undefined },
      h2: { fontSize: '24px', lineHeight: '1.35', fontWeight: undefined },
      h3: { fontSize: '20px', lineHeight: '1.4', fontWeight: undefined },
      h4: { fontSize: '16px', lineHeight: '1.45', fontWeight: undefined },
      h5: { fontSize: '14px', lineHeight: '1.5', fontWeight: undefined },
      h6: { fontSize: '12px', lineHeight: '1.5', fontWeight: undefined },
    },
  },
  primaryColor: 'blue',
  primaryShade: { light: 5, dark: 2 },
  defaultRadius: 'sm',
  shadows: {
    xs: '0 0 5px rgb(171 171 171 / 0.25)',
    sm: '0 0 10px rgb(171 171 171 / 0.25)',
    md: '0 0 20px rgb(171 171 171 / 0.35)',
    lg: '0 0 30px rgb(171 171 171 / 0.45)',
    darklg: '0 0 30px rgb(15 15 20 / 0.95)',
    xl: '0 0 50px rgb(171 171 171 / 0.45)',
  },
  colors,
  components,
  variantColorResolver,
})

/**
 * Builds CSS variables for our app's configured Mantine theme. Based on
 * what's found in Mantine's ThemeProvider, minus the React-specific bits.
 *
 * @param selector used for namespacing the variables
 * @returns CSS as a string, no style tag
 */
export function generateThemeVariables(selector = ':root') {
  const mergedTheme = mergeMantineTheme(DEFAULT_THEME, theme)
  const mergedVariables = defaultCssVariablesResolver(mergedTheme)
  return convertCssVariables(mergedVariables, selector)
}
